export const timelineStatusFunction = (currentGeneralStatus: number, billType: number) => {
    // DATA-4557: Filter out "effective" status from standard timeline steps
    // The "Effective" step is added manually in buildChevronTimelineSteps() with the correct date
    const effectiveStatusValue = DjangIO.app.bill.status.GeneralBillStatus.effective?.value

    let statusTimelineData = DjangIO.app.bill.status.GeneralBillStatus.region_supported_items()
        .filter((status: any) => {
            // Filter out "effective" status - it's added manually with effective_date
            if (effectiveStatusValue && status.value === effectiveStatusValue) {
                return false
            }

            if (billType === DjangIO.app.bill.models.BillType.nomination.value) {
                return [
                    DjangIO.app.bill.status.GeneralBillStatus.received.label,
                    DjangIO.app.bill.status.GeneralBillStatus.left_committee.label,
                    DjangIO.app.bill.status.GeneralBillStatus.confirmed.label,
                ].includes(status.label)
            } else if (billType === DjangIO.app.bill.models.BillType.eu_initiative.value) {
                return [
                    DjangIO.app.bill.status.GeneralBillStatus.eu_initiative_in_preparation.label,
                    DjangIO.app.bill.status.GeneralBillStatus.eu_initiative_commission_adoption.label,
                ].includes(status.label)
            } else {
                return ![
                    DjangIO.app.bill.status.GeneralBillStatus.unknown.label,
                    DjangIO.app.bill.status.GeneralBillStatus.eu_unknown.label,
                    DjangIO.app.bill.status.GeneralBillStatus.failed.label,
                    DjangIO.app.bill.status.GeneralBillStatus.received.label,
                    DjangIO.app.bill.status.GeneralBillStatus.left_committee.label,
                    DjangIO.app.bill.status.GeneralBillStatus.confirmed.label,
                    DjangIO.app.bill.status.GeneralBillStatus.returned.label,
                    DjangIO.app.bill.status.GeneralBillStatus.withdrawn.label,
                ].includes(status.label)
            }
        })
        .sort(DjangIO.valueSort)
    const globalMode = Userdata.isGlobalMode && Userdata.isGlobalMode() ? true : false
    if (currentGeneralStatus && globalMode) {
        const generalStatus = DjangIO.app.bill.status.GeneralBillStatus.by_value(currentGeneralStatus)
        const globalStatusEnum = DjangIO.app.bill.status.GlobalBillStatus
        const generalStatusEnum = DjangIO.app.bill.status.GeneralBillStatus
        const globalStatusMap = {
            [globalStatusEnum.introduced.value]: generalStatusEnum.global_introduced,
            [globalStatusEnum.in_progress.value]: generalStatusEnum.global_in_progress,
            [globalStatusEnum.adopted.value]: generalStatusEnum.global_adopted,
        }

        currentGeneralStatus = globalStatusMap[generalStatus.global_status].value
        statusTimelineData = [
            generalStatusEnum.global_introduced,
            generalStatusEnum.global_in_progress,
            generalStatusEnum.global_adopted,
        ]
    }

    return (
        currentGeneralStatus &&
        statusTimelineData.map((status: any) => ({
            label: status.short_label || status.label,
            isCompleted: currentGeneralStatus >= status.value,
            value: status.value,
        }))
    )
}

export const filterTimelineStatusForBillRegion = (
    statusTimelineData: Array<{ label: string; isCompleted: boolean; value: number }>,
    billRegionValue: number,
) => {
    if (!statusTimelineData || !statusTimelineData.length) return statusTimelineData

    const generalStatusEnum = DjangIO.app.bill.status.GeneralBillStatus

    const globalMode = Userdata.isGlobalMode && Userdata.isGlobalMode() ? true : false
    if (globalMode) return statusTimelineData

    return statusTimelineData.filter((entry) => {
        const statusEnumItem = generalStatusEnum.by_value(entry.value)
        const statusSupportedRegions: number[] | undefined = statusEnumItem && statusEnumItem.supported_regions
        if (!statusSupportedRegions || statusSupportedRegions.length === 0) return true
        return statusSupportedRegions.includes(billRegionValue)
    })
}

/**
 * Timeline step interface for the new chevron timeline component.
 */
export interface TimelineStep {
    id: string
    label: string
    date: string | null
    status: "completed" | "current" | "pending" | "vetoed" | "final-success"
    value?: number
    icon?: "check" | "x" | null
    effectiveDateSource?: number | null
    vetoType?: number | null
    vetoOverridden?: boolean
}

/**
 * Build timeline steps for the new chevron timeline with Effective Date and Vetoed support.
 *
 * This function creates timeline steps based on:
 * 1. Standard legislative steps from GeneralBillStatus enum
 * 2. Enacted step (if enacted_date exists)
 * 3. Effective Date step (if effective_date exists)
 * 4. Vetoed step (if is_vetoed is true)
 *
 * Special handling:
 * - When bill is vetoed (and not overridden), all future steps are removed
 * - When veto is overridden, Vetoed step remains visible but subsequent steps continue
 * - Effective Date step shows as "final-success" (green) if effective_now is true
 *
 * @param billData - Bill API response data
 * @returns Array of timeline steps with status, dates, and icons
 */
export const buildChevronTimelineSteps = (billData: any): TimelineStep[] => {
    if (!billData) return []

    const currentGeneralStatus = billData.current_general_status
    const billType = billData.bill_type
    const isVetoed = billData.is_vetoed
    const vetoType = billData.veto_type
    const enactedDate = billData.enacted_date
    const effectiveDate = billData.effective_date
    const effectiveDateSource = billData.effective_date_source
    const vetoDate = billData.veto_date
    const statusActions = billData.status_actions || []

    // Get standard legislative steps (including "Effective" if current_general_status=7)
    let standardSteps = timelineStatusFunction(currentGeneralStatus, billType) || []

    // Filter by region
    if (billData.region) {
        standardSteps = filterTimelineStatusForBillRegion(standardSteps, billData.region)
    }

    // Remove "Enacted" and "Effective" steps from standard steps - we'll add them manually
    // with the correct dates and status
    const enactedStatusValue = DjangIO.app.bill.status.GeneralBillStatus.enacted?.value
    const effectiveStatusValue = DjangIO.app.bill.status.GeneralBillStatus.effective?.value

    // Determine if veto was overridden by checking if bill has enacted_date
    // If enacted_date exists, either: (1) bill was never vetoed, or (2) veto was overridden
    // If enacted_date is null/undefined, veto stands and bill never became law
    // Using != instead of !== to catch both null and undefined (API may return either)
    const vetoOverridden = enactedDate != null

    // Remove Enacted and Effective steps if:
    // 1. We have enacted_date (we'll add Enacted manually with the correct date)
    // 2. Bill is vetoed and NOT overridden (veto stands, so bill never became law)
    // 3. We have effective_date (we'll add Effective manually with the correct date)
    if (enactedDate || (isVetoed && !vetoOverridden)) {
        standardSteps = standardSteps.filter(
            (step: any) => step.value !== enactedStatusValue && step.value !== effectiveStatusValue,
        )
    }

    const steps: TimelineStep[] = []

    // Helper function to find date for a step from status_actions (BillAction records)
    // status_actions contains the 6 main general status transitions with reliable dates
    // Note: Not all bills go through every step (e.g., some skip committee review)
    // so it's expected that some steps won't have dates
    const findDateForStep = (stepValue: number): string | null => {
        const action = statusActions.find((a: any) => a.action_type === stepValue)
        return action?.date || null
    }

    // Helper function to determine if a step is current (the active step)
    const isCurrentStep = (stepValue: number): boolean => {
        // If bill is vetoed and veto stands (not overridden), nothing after veto is "current"
        // The veto step itself will be marked as the final step
        if (isVetoed && !vetoOverridden) {
            return false
        }
        // Otherwise, current step is the one matching current_general_status
        return stepValue === currentGeneralStatus
    }

    // Build standard legislative steps
    standardSteps.forEach((step: any) => {
        const stepValue = step.value
        const isCurrent = isCurrentStep(stepValue)

        let status: TimelineStep["status"] = "pending"
        if (step.isCompleted && !isCurrent) {
            status = "completed"
        } else if (isCurrent) {
            status = "current"
        }

        steps.push({
            id: `step-${stepValue}`,
            label: step.label,
            date: findDateForStep(stepValue),
            status,
            value: stepValue,
            icon: null,
        })
    })

    // Handle Vetoed step - add BEFORE Enacted step (chronological order)
    if (isVetoed) {
        // All vetoed steps use "vetoed" status (light red background)
        // The visual distinction between overridden and final vetoes is whether
        // there are steps after it (overridden) or if it's the last step (final)
        steps.push({
            id: "vetoed",
            label: "Vetoed by President",
            date: vetoDate,
            status: "vetoed", // Always use "vetoed" status for light red color
            icon: "x",
            vetoType: vetoType, // Pass veto type for tooltip
            vetoOverridden: vetoOverridden, // Pass override status for tooltip
        })

        // If veto was NOT overridden, remove all future/pending steps
        // This makes the vetoed step the FINAL step in the timeline
        if (!vetoOverridden) {
            // Keep only completed steps and the vetoed step
            const filteredSteps = steps.filter(
                (s) => s.status === "completed" || s.status === "current" || s.status === "vetoed",
            )
            return filteredSteps
        }
    }

    // Add Enacted step if enacted_date exists (comes AFTER Vetoed if veto was overridden)
    if (enactedDate) {
        // Determine if Enacted step should be "current" or "completed"
        // Enacted is "current" if current_general_status=6 (Enacted)
        // Enacted is "completed" if current_general_status=7 (Effective)
        const isEnactedCurrent = currentGeneralStatus === enactedStatusValue
        const enactedStatus: TimelineStep["status"] = isEnactedCurrent ? "current" : "completed"

        steps.push({
            id: "enacted",
            label: "Enacted",
            date: enactedDate,
            status: enactedStatus,
            value: enactedStatusValue,
            icon: null,
            effectiveDateSource: effectiveDateSource, // Pass source to show tooltip on enacted step if effective date is assumed
        })
    }

    // Add Effective step if current_general_status=7 (Effective)
    // This means the bill has reached its effective date and the cronjob has updated the status
    if (currentGeneralStatus === effectiveStatusValue && effectiveDate) {
        steps.push({
            id: "effective",
            label: "Effective",
            date: effectiveDate, // Show date as badge below the step
            status: "final-success",
            icon: "check",
            effectiveDateSource: effectiveDateSource, // Pass source to show tooltip if assumed
        })
    }

    return steps
}
